import { Heart, Target, Map, Flame, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface CoreValue {
  id: string;
  value_name: string;
  description: string | null;
  importance_rank: number | null;
}

interface ValuesCelebrationProps {
  values: CoreValue[];
  onContinueToGoals: () => void;
  onReviewValues: () => void;
}

const VALUE_COLORS = [
  'border-l-primary',
  'border-l-accent',
  'border-l-blue-500',
  'border-l-purple-500',
  'border-l-green-500',
  'border-l-orange-500',
  'border-l-pink-500',
];

const roadmapSteps = [
  { icon: Heart, label: 'Values', done: true },
  { icon: Target, label: 'Goals', done: false, next: true },
  { icon: Map, label: 'Plan', done: false },
  { icon: Flame, label: 'Habits', done: false },
];

export const ValuesCelebration = ({
  values,
  onContinueToGoals,
  onReviewValues,
}: ValuesCelebrationProps) => {
  return (
    <div className="space-y-8 py-4">
      {/* Celebration Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex p-3 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 mb-2 animate-fade-in">
          <Heart className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-display font-bold animate-fade-in">
          Your Values Are Set!
        </h2>
        <p className="text-muted-foreground animate-fade-in">
          These values will guide your goals, habits, and coaching.
        </p>
      </div>

      {/* Value Cards with staggered animation */}
      <div className="grid gap-3 max-w-md mx-auto">
        {values.map((value, i) => (
          <Card
            key={value.id}
            className={`border-l-4 ${VALUE_COLORS[i % VALUE_COLORS.length]} animate-fade-in`}
            style={{ animationDelay: `${i * 150}ms`, animationFillMode: 'backwards' }}
          >
            <CardContent className="py-3 px-4">
              <p className="font-semibold text-sm">{value.value_name}</p>
              {value.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{value.description}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Roadmap */}
      <div className="flex items-center justify-center gap-2 text-sm">
        {roadmapSteps.map((step, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
              step.done
                ? 'bg-primary/10 text-primary'
                : step.next
                  ? 'bg-accent/10 text-accent font-medium'
                  : 'bg-muted text-muted-foreground'
            }`}>
              {step.done ? (
                <CheckCircle2 className="w-3.5 h-3.5" />
              ) : (
                <step.icon className="w-3.5 h-3.5" />
              )}
              <span>{step.label}</span>
            </div>
            {i < roadmapSteps.length - 1 && (
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50" />
            )}
          </div>
        ))}
      </div>

      {/* CTAs */}
      <div className="flex flex-col items-center gap-2">
        <Button onClick={onContinueToGoals} className="gap-2">
          Continue to Goals
          <ArrowRight className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onReviewValues} className="text-muted-foreground">
          Review my values
        </Button>
      </div>
    </div>
  );
};
